import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { MeResponse } from '@shared/api-types.js';
import { api } from '../lib/api.js';

const ME_KEY = ['auth', 'me'] as const;

export function useMe() {
  return useQuery({
    queryKey: ME_KEY,
    queryFn: () => api.auth.me(),
    staleTime: 60_000,
  });
}

export function useRequestCode() {
  return useMutation({
    mutationFn: (email: string) => api.auth.requestCode(email),
  });
}

export function useVerifyCode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { email?: string; code?: string; token?: string }) =>
      api.auth.verify(input),
    onSuccess: (data) => {
      // Seed the auth cache synchronously so the next render sees a signed-in
      // user. Without this, RequireAuth reads stale `user: null` between
      // verify-success and the background refetch, and bounces the user back
      // to /login — the symptom is /login?next=/welcome?next=… loops.
      qc.setQueryData<MeResponse>(ME_KEY, {
        user: {
          id: data.user.id,
          email: data.user.email,
          displayName: data.user.displayName,
          firstName: null,
          lastName: null,
          avatarKey: null,
          roles: data.user.roles,
          emailVerifiedAt: null,
          identityVerifiedAt: null,
          onboardedAt: data.user.onboardedAt,
          stripeConnectId: null,
        },
      });
      qc.invalidateQueries({ queryKey: ME_KEY });
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.auth.logout(),
    onSuccess: () => {
      qc.setQueryData(ME_KEY, { user: null });
      qc.invalidateQueries({ queryKey: ME_KEY });
    },
  });
}
